import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { User, HardHat } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppHeader from "@/components/AppHeader";
import { GoogleButton } from "@/pages/Login";

export default function Join() {
  const [params] = useSearchParams();
  const [role, setRole] = useState(params.get("type") === "professional" ? "professional" : "customer");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { ...form, role });
      setUser(data);
      navigate(role === "professional" ? "/pro/dashboard" : "/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen bg-[#F4F5F7]">
      <AppHeader />
      <div className="max-w-md mx-auto px-6 py-16 lg:py-24">
        <p className="uppercase tracking-[0.25em] text-flame font-heading font-bold text-sm mb-3">Join Prime Pro</p>
        <h1 className="font-heading font-black tracking-tighter text-navy text-4xl">Create your account</h1>

        <div className="grid grid-cols-2 gap-3 mt-8" data-testid="join-role-selector">
          {[
            { value: "customer", label: "I'm a Homeowner", Icon: User },
            { value: "professional", label: "I'm a Professional", Icon: HardHat },
          ].map(({ value, label, Icon }) => (
            <button key={value} type="button" data-testid={`join-role-${value}`}
              onClick={() => setRole(value)}
              className={`flex flex-col items-center gap-2 border p-5 text-sm font-medium transition-colors ${
                role === value ? "border-flame bg-white text-navy ring-2 ring-flame" : "border-border bg-white/60 text-slate-500 hover:border-slate-300"
              }`}>
              <Icon size={22} className={role === value ? "text-flame" : "text-slate-400"} />
              {label}
            </button>
          ))}
        </div>

        <form onSubmit={onSubmit} className="mt-6 space-y-4 bg-white border border-border p-8" data-testid="join-form">
          <div>
            <label className="text-sm font-medium text-navy block mb-2">Full name</label>
            <input data-testid="join-name-input" required value={form.name} onChange={set("name")}
              className="w-full h-14 border border-border px-4 text-sm focus:outline-none focus:ring-2 focus:ring-flame focus:border-transparent" />
          </div>
          <div>
            <label className="text-sm font-medium text-navy block mb-2">Email</label>
            <input data-testid="join-email-input" type="email" required value={form.email} onChange={set("email")}
              className="w-full h-14 border border-border px-4 text-sm focus:outline-none focus:ring-2 focus:ring-flame focus:border-transparent" />
          </div>
          <div>
            <label className="text-sm font-medium text-navy block mb-2">Password (min. 8 characters)</label>
            <input data-testid="join-password-input" type="password" required minLength={8} value={form.password} onChange={set("password")}
              className="w-full h-14 border border-border px-4 text-sm focus:outline-none focus:ring-2 focus:ring-flame focus:border-transparent" />
          </div>
          {error && <p data-testid="join-error" className="text-red-600 text-sm">{error}</p>}
          <button data-testid="join-submit-btn" disabled={loading}
            className="w-full bg-flame hover:bg-flame-dark disabled:opacity-60 text-white font-heading font-bold uppercase tracking-wider text-sm py-4 transition-colors">
            {loading ? "Creating account…" : "Create Account"}
          </button>
          <div className="flex items-center gap-3 py-1">
            <span className="flex-1 h-px bg-border" /><span className="text-xs text-slate-400">OR</span><span className="flex-1 h-px bg-border" />
          </div>
          <GoogleButton testId="join-google-btn" />
        </form>
        <p className="text-sm text-slate-500 mt-6">
          Already have an account?{" "}
          <Link to="/login" data-testid="join-login-link" className="text-flame font-medium hover:underline">Log in</Link>
        </p>
      </div>
    </div>
  );
}
